import { useState, useEffect, useMemo, ChangeEvent } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Drawer,
  Grid,
  Slider,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { addDays, format, startOfDay } from 'date-fns';
import { IconCheckbox } from '@tabler/icons';
// import clsx from 'clsx';

import { validateDecimalNumber, parseYmd } from '#root/utils';
import { travelerService, tripService } from '#root/services';
import { useResponsive, useNotification, useOptimisticAction } from '#root/hooks';
import { Input } from '#root/components/ui';
import { ArrowDownwardIcon, InboxIcon, DollarSignIcon, ClockIcon } from '#root/icons';
import DrawerAppbar from '../DrawerAppbar';
import OrderInfo from './OrderInfo';
import TripBox from './TripBox';
import ExtraData from './ExtraData';
import {
  validateOffer,
  getMissingLabels,
  getFirstErrorMessage,
  isPrefilledField,
  clampExpirationDate,
  MIN_DELIVERY_DAYS,
  OfferField,
} from './offerValidation';
import styles from './createOfferDrawer.module.scss';

const marks = [
  {
    value: 0,
    label: 'Sin caja',
  },
  {
    value: 50,
    label: 'Caja abierta',
  },
  {
    value: 100,
    label: 'Sellado',
  },
];

const initialOffer = {
  order_id: null,
  quantity: 1,
  real_price: '',
  traveler_fee: '',
  delivery_places: '',
  delivery_date: new Date(),
  expiration_date: new Date(),
  offer_message: '',
  packaging_type: '',
};

function valueLabelFormat(value) {
  return marks.findIndex((mark) => mark.value === value) + 1;
}

const CreateOfferDrawer = (props) => {
  const { open, onClose, order, removeOrder, restoreOrder } = props;
  const { isDesktop } = useResponsive();
  const { createMessage } = useNotification();
  const [offer, setOffer] = useState(initialOffer);
  const [trip, setTrip] = useState(null);
  const [tripError, setTripError] = useState(true);
  const [loadingTrip, setLoadingTrip] = useState(true);
  const [touched, setTouched] = useState<Partial<Record<OfferField, boolean>>>({});
  const [offerOptions, setOfferOptions] = useState({
    range_traveler_fee: {
      min: 0,
      max: 0,
    },
    current_offers: {
      quantity: 0,
      avg_fee: 0,
    },
  });
  const [loadingOfferOptions, setLoadingOfferOptions] = useState(false);
  const [slider, setSlider] = useState(50);

  // Estado derivado: una sola fuente para el boton, los mensajes inline y el aviso.
  const errors = useMemo(
    () => validateOffer(offer, { maxQuantity: order?.can ?? 1 }),
    [offer, order],
  );
  const missingLabels = getMissingLabels(errors);
  const hasErrors = missingLabels.length > 0;

  const markTouched = (field: OfferField) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  // Los campos que nacen vacios (precio, comision) esperan a que el viajero los
  // toque; los que llegan prellenados avisan de inmediato si quedan invalidos.
  const showError = (field: OfferField) =>
    Boolean(errors[field] && (touched[field] || isPrefilledField(field)));

  const fieldHelper = (field: OfferField) => (showError(field) ? errors[field] : undefined);

  // Cualquier error dentro del acordeon "Datos de entrega": colapsado es la unica
  // senal de que ahi adentro falta algo.
  const deliverySectionError =
    showError('delivery_places') || showError('delivery_date') || showError('expiration_date');

  // Hook para optimistic UI - crear oferta
  const createOfferAction = useOptimisticAction({
    // Acción del servidor
    action: (offerData) => travelerService.createOffer(offerData),

    // Optimistic update (inmediato)
    onOptimisticStart: (offerData, context) => {
      // Guardar contexto para rollback
      context.orderBackup = order;
      context.wasDrawerOpen = open;

      // Remover pedido de la lista (optimista)
      removeOrder(order);

      // Cerrar drawer inmediatamente
      handleClose();
    },

    // Success (confirmación)
    onSuccess: (result, offerData, context) => {
      // Ya se removió el pedido, no hacer nada más
      console.log('Oferta creada exitosamente:', result);
    },

    // Error (rollback)
    onError: (error, offerData, context) => {
      // Restaurar el pedido en la lista
      if (context?.orderBackup && restoreOrder) {
        restoreOrder(context.orderBackup);
      }
    },

    // Generar contexto para rollback
    getContext: (offerData) => ({
      orderBackup: order,
      wasDrawerOpen: open,
      timestamp: Date.now(),
    }),

    // Opciones
    options: {
      retry: 1, // Reintentar 1 vez si falla
      retryDelay: 2000, // Esperar 2 segundos antes de retry
      optimistic: true, // Habilitar optimistic updates
      showMessages: true, // Mostrar mensajes automáticos
      messages: {
        pending: 'Enviando oferta...',
        success: (result) => 'Oferta enviada exitosamente.',
        error: (error) => `No se pudo enviar la oferta. El pedido sigue disponible.`,
      },
    },
  });

  // El lugar de entrega lo impone el pedido cuando es Envio Nacional; si no, es el
  // del viaje. Unico sitio donde se decide.
  const getDeliveryPlace = () =>
    order?.flag_envio === 1 ? 'Oficina Mailboxes' : trip?.lugar_entrega ?? '';

  // Un viaje puede entregar hoy (la invariante solo exige fecha_entrega >= hoy),
  // pero una oferta nueva no: se corrige el prefill en vez de abrir el formulario
  // ya en error.
  const initialDeliveryDate = () => {
    const floor = addDays(startOfDay(new Date()), MIN_DELIVERY_DAYS);
    const fromTrip = parseYmd(trip?.fecha_entrega);
    return fromTrip && fromTrip > floor ? fromTrip : floor;
  };

  // El prefill del server ya viene como min(arrive_date, hoy + tope), pero se
  // recorta igual por si la entrega de la oferta quedara antes.
  const initialExpirationDate = () =>
    clampExpirationDate(parseYmd(trip?.offer_expiration_date) ?? new Date(), {
      arriveDate: trip?.arrive_date,
      deliveryDate: initialDeliveryDate(),
    });

  const handleClose = () => {
    // No resetea: de eso se encarga el efecto de apertura. Vaciar el estado aqui
    // se veria como un parpadeo, porque el Modal de MUI mantiene sus hijos
    // montados durante los ~195ms de la animacion de salida.
    onClose();
  };

  const handleSliderChange = (event, newValue) => {
    setSlider(newValue);
  };

  const handleInputChange = (e) => {
    markTouched(e.target.id);
    setOffer({
      ...offer,
      [e.target.id]: e.target.value,
    });
  };

  const handleInputNumberChange = (e) => {
    if (e.target.value === '' || validateDecimalNumber(e.target.value)) {
      markTouched(e.target.id);
      setOffer({
        ...offer,
        [e.target.id]: e.target.value,
      });
    }
  };

  const handleDateChange = (name: string) => (value) => {
    markTouched(name as OfferField);
    setOffer((prev) => {
      const next = { ...prev, [name]: value };
      // La vigencia no puede sobrevivir a la entrega: si el viajero la adelanta,
      // se recorta aqui para que la pantalla prometa lo que el server guardara.
      if (name === 'delivery_date') {
        next.expiration_date = clampExpirationDate(next.expiration_date, {
          arriveDate: trip?.arrive_date,
          deliveryDate: next.delivery_date,
        });
      }
      return next;
    });
  };

  const handleOfferQuantity = (e: ChangeEvent<HTMLInputElement>) => {
    // Solo filtra y redondea lo que se escribe; los mensajes los resuelve
    // `validateOffer`.
    if (validateDecimalNumber(e.target.value)) {
      const offer_quantity_fixed =
        parseFloat(e.target.value) < 0 ? 0 : Math.ceil(parseFloat(e.target.value));
      markTouched('quantity');
      setOffer({
        ...offer,
        [e.target.id]: offer_quantity_fixed,
        traveler_fee: '',
      });
    } else if (e.target.value === '') {
      markTouched('quantity');
      setOffer({
        ...offer,
        [e.target.id]: e.target.value,
        traveler_fee: '',
      });
    }
  };

  const handleInputRound = (name, value) => {
    setOffer({
      ...offer,
      [name]: Math.ceil(Number(value)),
    });
  };

  const setOrderDataToOffer = () => {
    // UNA sola escritura, y desde `initialOffer`. Antes eran dos seguidas que
    // partian del mismo `offer` del closure: `useState` reemplaza en vez de
    // mergear, asi que la segunda descartaba a la primera y `quantity` nunca
    // tomaba el `can` del pedido.
    setOffer({
      ...initialOffer,
      order_id: order?.idp,
      quantity: order?.can,
      packaging_type: order?.empaque,
      delivery_date: initialDeliveryDate(),
      expiration_date: initialExpirationDate(),
      delivery_places: getDeliveryPlace(),
    });
    setTouched({});
    if (order.empaque === 'sin caja') {
      setSlider(0);
    }
    if (order.empaque === 'caja abierta') {
      setSlider(50);
    }
    if (order.empaque === 'sellado') {
      setSlider(100);
    }
  };

  // Solo para cuando cambia el viaje: conserva lo que el viajero ya escribio.
  const setTripDataToOffer = () => {
    setOffer((prev) => ({
      ...prev,
      delivery_date: initialDeliveryDate(),
      expiration_date: initialExpirationDate(),
      delivery_places: getDeliveryPlace(),
    }));
  };

  // Red de seguridad: con el boton deshabilitado esto no deberia dispararse
  // nunca, pero cubre una carrera de estado o una regresion futura.
  const validateOfferCritical = () => {
    if (tripError) {
      return 'Necesitas un viaje vigente para ofertar';
    }
    const firstError = getFirstErrorMessage(errors);
    if (firstError) {
      return firstError;
    }
    // Validación de rango de comisión sugerida (warning, no bloqueante)
    const { min, max } = offerOptions?.range_traveler_fee || {};
    if (min && max && offer.traveler_fee) {
      const fee = parseFloat(offer.traveler_fee);
      const feePerUnit = offer.quantity > 1 ? fee / offer.quantity : fee;
      if (feePerUnit < min * 0.5 || feePerUnit > max * 2) {
        // Warning pero no bloquear
        console.warn(`Comisión fuera de rango sugerido: $${feePerUnit} (sugerido: $${min} - $${max})`);
      }
    }
    return null; // Sin errores críticos
  };

  const handleCreateOffer = async () => {
    // 1. Validaciones críticas (síncronas)
    const validationError = validateOfferCritical();
    if (validationError) {
      createMessage.error(validationError);
      return; // NO cerrar drawer
    }

    // 2. Construir objeto oferta
    let packagingType = '';
    if (slider === 0) {
      packagingType = 'sin caja';
    }
    if (slider === 50) {
      packagingType = 'caja abierta';
    }
    if (slider === 100) {
      packagingType = 'sellado';
    }

    const arg = {
      ...offer,
      order_id: order.idp,
      delivery_places: String(offer.delivery_places ?? '').trim(),
      delivery_date: format(new Date(offer?.delivery_date), 'yyyy-MM-dd'),
      expiration_date: format(new Date(offer?.expiration_date), 'yyyy-MM-dd'),
      packaging_type: packagingType,
    };

    // 3. Ejecutar acción optimista (NO await para no bloquear)
    createOfferAction.execute(arg).catch((error) => {
      // Error ya manejado por onError, solo log para debugging
      console.error('Error creating offer:', error);
    });
  };

  const fetchGetCurrentTrip = async () => {
    setLoadingTrip(true);
    const res = await tripService.getCurrent();
    // `/trips/current` responde HTTP 200 incluso cuando falla (response_failed),
    // asi que fetchApi no lo marca como error: el viaje real se reconoce por su `id`.
    const currentTrip = !res.info.error && res.data?.id ? res.data : null;
    if (!currentTrip) {
      setTrip(null);
      setTripError(true);
      setLoadingTrip(false);
      return;
    }
    setTrip(currentTrip);
    // Vigencia para ofertar: unica fuente de verdad es el server.
    // `undefined` (backend sin G1) se trata como NO ofertable a proposito.
    setTripError(!(currentTrip.is_offerable === true));
    setLoadingTrip(false);
  };

  const resetOfferOptions = () => {
    setLoadingOfferOptions(true);
    setOfferOptions({
      range_traveler_fee: {
        min: 0,
        max: 0,
      },
      current_offers: {
        quantity: 0,
        avg_fee: 0,
      },
    });
    setLoadingOfferOptions(false);
  };

  const fetchOfferOptions = async () => {
    setLoadingOfferOptions(true);
    const res = await travelerService.getOrderOfferOptions(order.idp);
    if (res.error) return setLoadingOfferOptions(false);
    if (!res.data) return setLoadingOfferOptions(false);
    setOfferOptions(res.data);
    setLoadingOfferOptions(false);
  };

  useEffect(() => {
    fetchGetCurrentTrip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El formulario se inicializa CUANDO SE ABRE. Colgado solo de `order` no se
  // disparaba al reabrir el mismo pedido: la prop es la misma referencia, React
  // hace bail-out y el efecto nunca corria.
  useEffect(() => {
    if (!open) return;
    resetOfferOptions();
    setOrderDataToOffer();
    fetchOfferOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, open]);

  useEffect(() => {
    if (trip) {
      setTripDataToOffer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip]);

  const content = (
    <div className={styles.root}>
      <OrderInfo order={order} />

      {!tripError && offerOptions && (
        <div className={styles.offerOptions}>
          {loadingOfferOptions ? (
            <p>cargando...</p>
          ) : (
            <>
              <ClockIcon />
              <div>
                {offerOptions?.range_traveler_fee?.min &&
                  offerOptions?.range_traveler_fee?.max && (
                    <p>
                      Comisión sugerida por Guvery: ${' '}
                      {Number(offerOptions.range_traveler_fee.min)} a{' '}
                      {Number(offerOptions.range_traveler_fee.max)}
                      {order.can > 1 && <span>&nbsp;(x unidad)</span>}.
                    </p>
                )}
                {!offerOptions.current_offers?.quantity ? (
                  <p>Sé el primero en hacer una oferta</p>
                ) : (
                  <p>
                    {offerOptions.current_offers.quantity > 1 ? (
                      <>Tiene {offerOptions.current_offers.quantity} ofertas por $ {offerOptions.current_offers.avg_fee} en promedio</>
                    ) : (
                      <>Tiene 1 oferta por $ {offerOptions.current_offers.avg_fee}</>
                    )}
                    {order.can > 1 && <span>&nbsp;(x unidad)</span>}.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div className={styles.subTitle}>
        <h4>Mi oferta es:</h4>
      </div>
      <TripBox
        createMessage={createMessage}
        trip={trip}
        tripError={tripError}
        fetchTrips={fetchGetCurrentTrip}
        loading={loadingTrip}
      />

      {loadingTrip ? (
        <div className={styles.offerForm}>
        <Alert severity="info">
          Cargando viaje vigente...
        </Alert>
      </div>
      ) : (
        <>
        {!tripError ? (
        <>
          <div className={styles.offerForm}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <div className={styles.slider}>
                  <Slider
                    valueLabelFormat={valueLabelFormat}
                    aria-labelledby="discrete-slider-restrict"
                    step={null}
                    // valueLabelDisplay="auto"
                    marks={marks}
                    value={slider}
                    onChange={handleSliderChange}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={4}>
                <Input
                  id="quantity"
                  type="tel"
                  inputMode="numeric"
                  label="Cantidad"
                  value={offer.quantity}
                  onChange={handleOfferQuantity}
                  onBlur={() => {
                    markTouched('quantity');
                    handleInputRound('quantity', offer.quantity);
                  }}
                  error={showError('quantity')}
                  helperText={fieldHelper('quantity')}
                  disabled={order.can === 1}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <Input
                  id="real_price"
                  type="tel"
                  inputMode="decimal"
                  label={offer.quantity > 1 ? 'Precio real total' : 'Precio real'}
                  value={offer.real_price}
                  onChange={handleInputNumberChange}
                  onBlur={() => {
                    markTouched('real_price');
                    handleInputRound('real_price', offer.real_price);
                  }}
                  error={showError('real_price')}
                  helperText={fieldHelper('real_price')}
                  startAdornment={<DollarSignIcon style={{ fontSize: 16 }} />}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <Input
                  id="traveler_fee"
                  type="tel"
                  inputMode="decimal"
                  label={offer.quantity > 1 ? 'Tu comisión total' : 'Tu comisión'}
                  value={offer.traveler_fee}
                  onChange={handleInputNumberChange}
                  onBlur={() => {
                    markTouched('traveler_fee');
                    handleInputRound('traveler_fee', offer.traveler_fee);
                  }}
                  error={showError('traveler_fee')}
                  helperText={fieldHelper('traveler_fee')}
                  startAdornment={<DollarSignIcon style={{ fontSize: 16 }} />}
                />
              </Grid>
            </Grid>
          </div>
          <ExtraData
            offer={offer}
            loading={loadingTrip}
            arriveDate={trip?.arrive_date}
            flagEnvio={order.flag_envio === 1 ? true : false}
            handleDateChange={handleDateChange}
            handleInputChange={handleInputChange}
            deliveryPlacesError={fieldHelper('delivery_places')}
            deliveryDateError={fieldHelper('delivery_date')}
            hasSectionError={deliverySectionError}
            onDeliveryPlacesBlur={() => markTouched('delivery_places')}
          />
          <div className={styles.offerForm}>
            <Accordion className={styles.acordion}>
              <AccordionSummary
                expandIcon={<ArrowDownwardIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header">
                <div className={styles.header}>
                  <h5>
                    Mensaje opcional: <strong>{offer.offer_message}</strong>
                  </h5>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <Input
                  multiline
                  rows={4}
                  id="offer_message"
                  value={offer.offer_message}
                  onChange={handleInputChange}
                />
              </AccordionDetails>
            </Accordion>
          </div>
          <div className={styles.offerHelp}>
            <IconCheckbox />
            <span>Simulé la compra para incluir el tax/shipping en el Precio Real.</span>
          </div>
          <div className={styles.action}>
            <Button
              fullWidth
              disableElevation
              color="primary"
              variant="contained"
              size="large"
              startIcon={<InboxIcon />}
              disabled={hasErrors}
              onClick={handleCreateOffer}
              style={{ height: 48 }}>
              Enviar Oferta
            </Button>
            {hasErrors && (
              <p className={styles.actionHint}>
                Falta completar: {missingLabels.join(', ')}
              </p>
            )}
          </div>
        </>
      ) : (
        <div className={styles.offerForm}>
          <Alert severity="error">
            Tu viaje seleccionado ya no está vigente para ofertar (fecha de viaje
            vencida). Selecciona o crea un viaje con fecha de viaje igual o posterior a
            hoy.
          </Alert>
        </div>
      )}
        </>
      )}

      
    </div>
  );

  return (
    <>
      {isDesktop ? (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 0,
            style: {
              background: 'var(--color-background-base)',
              // borderRight: 'none',
              width: 576,
              height: '100%',
            },
          }}>
          <DrawerAppbar title={'Pedido #' + order.id_public} onClose={handleClose} />
          {content}
        </Drawer>
      ) : (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              width: '100%',
              background: 'var(--color-background-base)',
            },
          }}
          style={{
            zIndex: 1300,
          }}>
          <DrawerAppbar isMobile title={'Pedido #' + order.id_public} onClose={handleClose} />
          {content}
        </Drawer>
      )}
    </>
  );
};

export default CreateOfferDrawer;
